import { useSentryQuery } from '../utils/sentryQueryWrapper';
import { RegionResponse } from '@/types/region';
import { fetchAreaByProvinceCode, fetchListProvinces } from '@/api/regionService';

// Iraq's provinces are reference data — they do not change while a merchant has
// the tab open. The global default is refetchOnMount:true with no staleTime, so
// this 1.37 MB / 5,139-row payload was re-downloaded, re-parsed and re-compared
// on EVERY mount of EVERY screen that shows a province picker — and there are
// thirteen of them (create order, edit order, resend, locations, address book,
// shipping rules, service level…). Navigating between any two of them paid for
// it again.
//
// One hour, not Infinity: a genuinely new province still appears without the
// merchant having to hard-reload, and a page refresh always refetches anyway.
const ONE_HOUR_MS = 60 * 60 * 1000;

// Fetch List Provinces
//
// `scope: 'pickup'` asks for provinces we COLLECT from (activated branches plus
// Baghdad) instead of the full reference list. Pass it ONLY from a pickup-origin
// form — a destination picker or a shipping-rule condition needs every province,
// and narrowing those stops merchants shipping anywhere unconfigured.
//
// The scope is part of the cache key: without it the first caller to mount would
// decide what every other picker sees for the next hour.
export const useFetchListProvinces = (scope?: 'pickup') => {
  return useSentryQuery<RegionResponse, Error>(['region', scope ?? 'all'], () => fetchListProvinces(scope), {
    staleTime: ONE_HOUR_MS,
    // Nothing on this screen reacts to a province list changing mid-session, so
    // a refetch can only ever hand back an identical array — at the cost of a
    // deep structural-sharing compare over 5,139 objects.
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Fetch List Province By Code
export const useFetchAreaByProvinceCode = (ProvinceCode: string) => {
  return useSentryQuery<RegionResponse, Error>(
    ['region', ProvinceCode],
    () => fetchAreaByProvinceCode(ProvinceCode),
    {
      enabled: !!ProvinceCode,
    },
  );
};
